import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { File } from 'v2/models';
import styles from './FilePath.module.scss';
import Measure, { BoundingRect } from 'react-measure';
import { getPartsToDisplay } from './getPartsToDisplay';

interface FilePathProps {
  file: File;
}
export const FilePath = ({ file }: FilePathProps) => {
  const { name = '', relative_path = '' } = file;
  const [nameBounds, setNameBounds] = useState<BoundingRect | undefined>();

  const [pathBounds, setPathBounds] = useState<BoundingRect | undefined>();
  const rootRef = useRef<HTMLDivElement>(null);

  const approximateDisplayNameFontSize = parseFloat(
    rootRef.current ? getComputedStyle(rootRef.current).fontSize : '12'
  );

  const { parts, isPreventNameCollapse } = getPartsToDisplay(
    nameBounds?.width,
    pathBounds?.width,
    name,
    relative_path,
    approximateDisplayNameFontSize
  );

  const nameStyle: React.CSSProperties = {
    minWidth: `${nameBounds?.width}px`,
  };

  return (
    <div className={styles.root}>
      <div className={styles.measureContainer}>
        <Measure
          bounds
          onResize={(rect) => setNameBounds(rect.bounds)}
          innerRef={rootRef}
        >
          {({ measureRef }) => (
            <div
              aria-hidden
              ref={measureRef}
              className={classnames(styles.measure, styles.segment)}
            >
              {name}
            </div>
          )}
        </Measure>
      </div>

      <Measure bounds onResize={(rect) => setPathBounds(rect.bounds)}>
        {({ measureRef: pathRef }) => (
          <div ref={pathRef} className={classnames(styles.path)}>
            {parts.map((part, index) => {
              const isSeparator = part === '/';
              const isFilename = index === parts.length - 1;
              // this is a react list of components so we must provide unique keys.
              // We de-duplicate compnent keys for separators `/` by appending the index
              // if they are not sparators we can just use the part as the key
              const key = isSeparator ? part + index : part;
              // separators parts ie `/` don't collapse,
              // they have flex-shrink and flex-grow unset by the .separator class
              const classes = classnames(styles.segment, {
                [styles.separator]: isSeparator,
              });
              // we only prevent truncation on the filename part which is always the last part
              // of all possible parts.
              const preventNameTruncation = isPreventNameCollapse && isFilename;
              // we prevent filename truncation by specifying a value for minWidth as inline style
              // this minWidth depends on the actual width of the rendered text, which is
              // what why we use react measure for the .measureContainer
              const style = preventNameTruncation ? nameStyle : undefined;
              // the child content of the div is just the part, be it a separator `/`,
              // the path name or the file name
              const content = part;
              return (
                <div
                  className={classes}
                  style={style}
                  key={key}
                  data-testid={key}
                >
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </Measure>
    </div>
  );
};
